import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import * as menuApi from '../../shared/api/menus.js'
import type { Menu } from '../../shared/types/models.js'
import { canManageCatalog } from '../../shared/utils/permissions.js'
import type { LifecycleAction } from '../../shared/utils/catalog.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import StatusBadge from './components/StatusBadge.js'
import MenuEditForm from './components/MenuEditForm.js'
import LifecycleActions from './components/LifecycleActions.js'
import CategoriesSection from './components/CategoriesSection.js'
import MenuAvailabilitySection from './components/MenuAvailabilitySection.js'

type DetailStatus = 'loading' | 'ready' | 'error' | 'notFound'

export default function MenuDetailPage() {
  const { t } = useTranslation()
  const { menuId } = useParams<{ menuId: string }>()
  const navigate = useNavigate()

  const orgId = useOrgStore(s => s.activeOrganization)?.id
  const role = useOrgStore(s => s.activeOrganization)?.role
  const canManage = canManageCatalog(role)

  const [menu, setMenu] = useState<Menu | null>(null)
  const [status, setStatus] = useState<DetailStatus>('loading')
  const [actionError, setActionError] = useState('')

  async function load() {
    if (!orgId || !menuId) return
    setStatus('loading')
    try {
      const data = await menuApi.getMenu(orgId, menuId)
      setMenu(data)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 404 ? 'notFound' : 'error')
    }
  }

  useEffect(() => {
    void load()
  }, [orgId, menuId])

  async function handleLifecycle(action: LifecycleAction) {
    if (!orgId || !menu) return
    setActionError('')
    try {
      const updated =
        action === 'suspend'
          ? await menuApi.suspendMenu(orgId, menu.id)
          : action === 'activate'
            ? await menuApi.activateMenu(orgId, menu.id)
            : await menuApi.archiveMenu(orgId, menu.id)
      setMenu(updated)
    } catch {
      setActionError(t('menu.lifecycleError'))
    }
  }

  if (status === 'loading') {
    return (
      <section className="page-section">
        <h2>{t('app.menu')}</h2>
        <div className="page-loading">
          <div className="spinner" />
        </div>
      </section>
    )
  }

  if (status === 'notFound' || status === 'error' || !menu) {
    return (
      <section className="page-section">
        <div className="page-header">
          <Link to={ROUTES.MENU} className="back-link">
            ← {t('menu.back')}
          </Link>
        </div>
        <div className="settings-card">
          <div className="form-error-banner">
            {status === 'notFound' ? t('menu.notFound') : t('menu.loadError')}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (status === 'notFound') navigate(ROUTES.MENU)
              else void load()
            }}
          >
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  const readOnly = !canManage || menu.status === 'ARCHIVED'
  if (!orgId) return null

  return (
    <section className="page-section page-section--wide">
      <Link to={ROUTES.MENU} className="back-link">
        ← {t('menu.back')}
      </Link>

      <div className="page-header menu-detail-header">
        <div>
          <h2>{menu.name}</h2>
          <p className="menu-detail-slug">{menu.slug ?? '—'}</p>
        </div>
        <StatusBadge status={menu.status} />
      </div>

      <MenuEditForm organizationId={orgId} menu={menu} disabled={readOnly} />

      <MenuAvailabilitySection
        organizationId={orgId}
        menuId={menu.id}
        menuArchived={menu.status === 'ARCHIVED'}
        canManage={canManage}
      />

      {actionError && <div className="form-error-banner">{actionError}</div>}

      {canManage && menu.status !== 'ARCHIVED' && (
        <div className="settings-card lifecycle-card">
          <h3>{t('menu.lifecycle')}</h3>
          <LifecycleActions
            status={menu.status}
            entityName={menu.name}
            disabled={!canManage}
            onAction={handleLifecycle}
          />
        </div>
      )}

      <CategoriesSection organizationId={orgId} menuId={menu.id} menuStatus={menu.status} canManage={canManage} />
    </section>
  )
}